import type {ReactElement} from 'react';
import {useState} from 'react';
import type {NotificationsApprovalButtonsProps} from './notifications-approval-buttons.types';
import type {NotificationsCTA, NotificationsItemData} from '@Panels/notifications-panel/notifications-panel.types';
import {NotificationApprovalStatus} from '@Panels/notifications-panel/notifications-panel.types';
import {Button, ButtonSize, ButtonType, PROPER_NOUN_BUTTON_CLASS} from '@Components/button';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {markClickedNotificationAsRead} from '@Libraries/notifications-panel-library';
import {useAllNotifications} from '@Panels/notifications-panel/notifications-panel-hooks';
import styles from './notifications-approval-buttons.module.scss';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';

export function NotificationsApprovalButtons({...props}: NotificationsApprovalButtonsProps): ReactElement[] | null {
  const allFilteredNotifications = useAllNotifications();
  const itemData = JSON.parse(props.notification.itemData) as NotificationsItemData;
  const [loadingMap, setLoadingMap] = useState<Record<number, boolean>>({});
  const [approvalState, setApprovalState] = useState<NotificationApprovalStatus | null>(itemData.approvalStatus ?? null);

  const getButtonTextElement = (buttonText: string, buttonClasses: string, buttonIcon: string): ReactElement => (
    <div className="flex-h-row _full-width align-items-center">
      <Icon size={IconSize.SIZE_ICON_16} icon={buttonIcon} />
      <Text className={buttonClasses} size={TextSize.XXSMALL} val={buttonText} />
    </div>
  );

  const getButtonClasses = (notificationButton: NotificationsCTA): string => (notificationButton.hasProperNoun ? `flex-1 ${PROPER_NOUN_BUTTON_CLASS}` : 'flex-1');

  const getButtonsForApproval = (buttons: NotificationsCTA[], approvalValue: NotificationApprovalStatus | null): ReactElement => (
    <div key={`approval-buttons-${approvalValue ?? 'pending'}`} className={`${styles.buttonsGap} flex-h-row ${buttons.length > 1 ? '_full-width' : styles.halfWidth}`}>
      {buttons.map((notificationButton: NotificationsCTA, index: number) => (
        <Button
          key={index}
          icon={notificationButton.icon ?? ''}
          onClick={() => {
            window.PMW.gtm.trackGA4CustomEvent(GA4EventName.NOTIFICATION_CLICK, {[GA4EventParamName.VALUE]: props.notification.notificationCategory});
            setLoadingMap((prev) => ({...prev, [index]: true}));
            if (notificationButton.ajaxUri) {
              void window.PMW.readLocal(notificationButton.ajaxUri).finally(() => {
                setLoadingMap((prev) => ({...prev, [index]: false}));
              });
            }
            markClickedNotificationAsRead(props.notification, allFilteredNotifications, notificationButton.link ?? '', approvalValue ?? notificationButton.approvalValue);
            setApprovalState(approvalValue ?? notificationButton.approvalValue ?? null);
          }}
          text={notificationButton.text ?? ''}
          size={ButtonSize.SMALL}
          type={notificationButton.type ?? ButtonType.PRIMARY}
          isFullWidth
          customClasses={getButtonClasses(notificationButton)}
          isLoading={loadingMap[index]}
        />
      ))}
    </div>
  );

  const getButtons = (): ReactElement[] | null => {
    const buttons = itemData.buttons;
    if (!buttons || buttons.length === 0) return null;

    const buttonsToDisplay: ReactElement[] = [];

    switch (approvalState) {
      case NotificationApprovalStatus.APPROVED: {
        const approvalButton = buttons[1];
        buttonsToDisplay.push(getButtonTextElement(approvalButton.textAfterSuccess ?? '', 'spacing-p-l-1', approvalButton.iconForTextAfterSuccess ?? ''));
        break;
      }

      case NotificationApprovalStatus.DECLINED: {
        const declineButton = buttons[0];
        buttonsToDisplay.push(getButtonTextElement(declineButton.textAfterSuccess ?? '', 'spacing-p-l-1', declineButton.iconForTextAfterSuccess ?? ''));
        break;
      }

      case NotificationApprovalStatus.NOT_REQUIRED:
        buttonsToDisplay.push(getButtonsForApproval(buttons, NotificationApprovalStatus.NOT_REQUIRED));
        break;

      case NotificationApprovalStatus.PENDING:
        buttonsToDisplay.push(getButtonsForApproval(buttons, null));
        break;

      default:
        return null;
    }

    return buttonsToDisplay.length > 0 ? buttonsToDisplay : null;
  };

  return getButtons();
}
